import type { JSX } from "react";
import { useState } from "react";
import styled from "styled-components";
import { currentColors } from "../colors";
import useClickOutside from "../hooks/useClickOutside";

export type DefaultSelectOptions = "all" | "some" | "none";

interface IProps<Opts extends string> {
    selected: DefaultSelectOptions;
    onSelect: (selected: Opts | DefaultSelectOptions) => void;
    dropOptions: {
        value: Opts;
        label: string;
    }[];
    className?: string;
}

const Container = styled.div`
    position: relative;
    width: fit-content;
    color: ${currentColors.grey3};
`;

const Dropdown = styled.div`
    position: absolute;
    z-index: 1;
    background: white;
    color: ${currentColors.text};
    box-shadow: 0px 8px 10px 1px rgba(0,0,0,.14),0px 3px 14px 2px rgba(0,0,0,.12),0px 5px 5px -3px rgba(0,0,0,.2);
`;

const DropdownItem = styled.div`
    padding: 0.5rem 1rem;
    cursor: pointer;
    white-space: nowrap;

    &:hover {
        background-color: ${currentColors.grey2};
    }
`;

const Svg = styled.svg`
    height: 20px;
`;

const Rect = styled.rect`
    cursor: pointer;
`;

const Line = styled.line`
    cursor: pointer;
`;

const Path = styled.path`
    cursor: pointer;
`;

const Polygon = styled.polygon`
    cursor: pointer;
`;

function CheckboxDropdown<Opts extends string>({
    selected,
    dropOptions,
    onSelect,
    className,
}: IProps<Opts>): JSX.Element {
    const [open, setOpen] = useState(false);
    const ref = useClickOutside<HTMLDivElement>(() => {
        setOpen(false);
    });
    const selectDropdownValue = (val: Opts) => {
        setOpen(false);
        onSelect(val);
    };
    const clickRect = () => {
        if (selected === "none") {
            onSelect("all");
        } else {
            onSelect("none");
        }
    };
    const toggleDropdown = () => {
        setOpen((o) => !o);
    };
    return (
        <Container className={className}>
            <Svg viewBox="0 0 36 16">
                <Rect
                    x="1"
                    y="1"
                    width="14"
                    height="14"
                    rx="2"
                    ry="2"
                    stroke="currentColor"
                    strokeWidth="2"
                    fill="white"
                    onClick={clickRect}
                />
                {selected === "some" && (
                    <Line x1="4" y1="8" x2="12" y2="8" strokeWidth="2" stroke="currentColor" onClick={clickRect} />
                )}
                {selected === "all" && (
                    <Path d="M4 8 L7 11 L12 4" fill="none" strokeWidth="2" stroke="currentColor" onClick={clickRect} />
                )}
                <Rect
                    x="19"
                    y="0"
                    width="18"
                    height="16"
                    fill={open ? currentColors.grey2 : "white"}
                    onClick={toggleDropdown}
                />
                {dropOptions.length > 0 && (
                    <Polygon points="22,4 34,4 28,12" fill="currentColor" onClick={toggleDropdown} />
                )}
            </Svg>
            {open && (
                <Dropdown ref={ref}>
                    {dropOptions.map((option) => (
                        <DropdownItem key={option.value} onClick={() => selectDropdownValue(option.value)}>
                            {option.label}
                        </DropdownItem>
                    ))}
                </Dropdown>
            )}
        </Container>
    );
}

export default CheckboxDropdown;
